import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllSessions,
  clearSessions,
} from "./../../actions/sessionActions";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Collections,
  EmojiEvents,
  Category,
  SubdirectoryArrowRight,
  MenuBook,
  ExpandMore,
  ExpandLess,
  ViewList,
  ViewModule,
} from "@mui/icons-material";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import SessionTableRow from "./SessionTableRow";
import SessionFilterHeader from "./SessionFilterHeader";
import './SessionsTable.scss';

const SessionsTable = ({ searchText = "", allExpanded = true, isGroupedView = false }) => {
  const dispatch = useDispatch();
  const all_sessions = useSelector((state) => state.sessions.all_sessions);
  const loading = useSelector((state) => state.sessions.isLoading);
  const sectionKeys = [
    'category',
    'subcategory',
    'series',
    'challenge',
    'audiobook',
    'unparented'
  ];
  const [expandedSections, setExpandedSections] = useState(() =>
    Object.fromEntries(sectionKeys.map(key => [key, allExpanded]))
  );

  // State for column filters
  const [recommendedFilter, setRecommendedFilter] = React.useState('all');
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [premiumFilter, setPremiumFilter] = React.useState('all');
  const [duplicateFilter, setDuplicateFilter] = React.useState('all');

  // State for view mode
  const [viewMode, setViewMode] = React.useState(isGroupedView ? 'grouped' : 'list');

  // Sync expandedSections with allExpanded prop
  React.useEffect(() => {
    setExpandedSections(Object.fromEntries(sectionKeys.map(key => [key, allExpanded])));
  }, [allExpanded]);

  // Sync viewMode with isGroupedView prop
  React.useEffect(() => {
    setViewMode(isGroupedView ? 'grouped' : 'list');
  }, [isGroupedView]);

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleExpandCollapseAll = () => {
    const newExpandedState = !Object.values(expandedSections).every(Boolean);
    setExpandedSections(Object.fromEntries(sectionKeys.map(key => [key, newExpandedState])));
  };

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  useEffect(() => {
    dispatch(clearSessions());
    dispatch(fetchAllSessions());
  }, [dispatch]);

  const getParentName = (session) => {
    if (session.category_title) return session.category_title;
    if (session.subcategory_title) return session.subcategory_title;
    if (session.series_title) return session.series_title;
    if (session.challenge_title) return session.challenge_title;
    return "None";
  };

  const groupSessionsByParent = (sessions) => {
    const groups = {
      category: [],
      subcategory: [],
      series: [],
      challenge: [],
      audiobook: [],
      unparented: []
    };

    sessions.forEach(session => {
      if (session.category_id) {
        groups.category.push(session);
      } else if (session.subcategory_id) {
        if (session.subcategory_is_book ) {
          groups.audiobook.push(session);
        } else {
          groups.subcategory.push(session);
        }
      } else if (session.series_id) {
        groups.series.push(session);
      } else if (session.challenge_id) {
        groups.challenge.push(session);
      } else {
        groups.unparented.push(session);
      }
    });

    return groups;
  };

  const filteredSessions = all_sessions?.filter((session) => {
    const search = searchText.toLowerCase();
    const sessionName = session.session_name?.toLowerCase() || '';
    const parentName = getParentName(session)?.toLowerCase() || '';
    // Column filters
    if (recommendedFilter !== 'all' && (session.session_is_recommended  ? 'yes' : 'no') !== recommendedFilter) return false;
    if (activeFilter !== 'all' && (session.session_is_active  ? 'active' : 'inactive') !== activeFilter) return false;
    if (premiumFilter !== 'all' && (session.session_is_free ? 'free' : 'premium') !== premiumFilter) return false;
    if (duplicateFilter !== 'all' && (session.session_is_duplicate  ? 'yes' : 'no') !== duplicateFilter) return false;
    return sessionName.includes(search) || parentName.includes(search);
  })?.sort((a, b) => {
    // Sort by session_created_at descending (newest first)
    const dateA = new Date(a.session_created_at);
    const dateB = new Date(b.session_created_at);
    return dateB - dateA;
  });

  if (loading && !all_sessions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!filteredSessions?.length) {
    return (
      <TableContainer component={Paper} sx={{ marginTop: 3 }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {searchText ? "No sessions found matching your search" : "No sessions available"}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  const groupedSessions = groupSessionsByParent(filteredSessions);

  const filterHeaderProps = {
    activeFilter,
    onActiveFilterChange: e => setActiveFilter(e.target.value),
    recommendedFilter,
    onRecommendedFilterChange: e => setRecommendedFilter(e.target.value),
    duplicateFilter,
    onDuplicateFilterChange: e => setDuplicateFilter(e.target.value),
    premiumFilter,
    onPremiumFilterChange: e => setPremiumFilter(e.target.value),
  };

  const renderFullListTable = () => {
    return (
      <TableContainer component={Paper} sx={{ marginTop: 2 }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <SessionFilterHeader {...filterHeaderProps} />
          <TableBody>
            {filteredSessions.map((session) => (
              <SessionTableRow key={session.session_id + session.session_name} session={session} parentName={getParentName(session)} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSection = (sessions, title, icon, sectionKey) => {
    if (!sessions.length) return null;

    return (
      <Accordion
        expanded={expandedSections[sectionKey]}
        onChange={handleAccordionChange(sectionKey)}
        slotProps={{
          transition: {
            timeout: 0
          }
        }}
        sx={{
          '&:before': { display: 'none' },
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          mb: 1,
          '& .MuiAccordionDetails-root': {
            padding: 0,
          },
          '& .MuiCollapse-root': {
            transition: 'none',
          },
          '& .MuiAccordionSummary-root': {
            transition: 'none',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transition: 'none',
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f0f0f0',
            '&:hover': { backgroundColor: '#e8e8e8' },
            minHeight: '32px !important',
            height: '32px',
            '&.Mui-expanded': {
              minHeight: '32px !important',
              height: '32px !important'
            },
            '& .MuiAccordionSummary-content': {
              margin: '4px 0',
              '&.Mui-expanded': {
                margin: '4px 0'
              }
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {icon && React.cloneElement(icon, { sx: { fontSize: 18 } })}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
              {title} ({sessions.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ width: '100%' }}>
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <SessionFilterHeader {...filterHeaderProps} />
                <TableBody>
                  {sessions.map((session) => (
                    <SessionTableRow key={session.session_id + session.session_name} session={session} parentName={getParentName(session)} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box className="sessions-table-container">
      <LoadingBackdrop open={loading && all_sessions !== null} />

      {/* View Toggle Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>
          Sessions ({filteredSessions?.length || 0})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.5,
                fontSize: '0.8rem'
              }
            }}
          >
            <ToggleButton value="grouped" aria-label="grouped view">
              <ViewModule sx={{ fontSize: 16, mr: 0.5 }} />
              Grouped
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewList sx={{ fontSize: 16, mr: 0.5 }} />
              Full List
            </ToggleButton>
          </ToggleButtonGroup>
          {viewMode === 'grouped' && (
            <Tooltip title={Object.values(expandedSections).every(Boolean) ? "Collapse All" : "Expand All"}>
              <IconButton
                onClick={handleExpandCollapseAll}
                size="small"
                sx={{
                  ml: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'primary.main'
                  }
                }}
              >
                {Object.values(expandedSections).every(Boolean) ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {viewMode === 'grouped' ? (
        <Box>
          {renderSection(groupedSessions.category, 'Category Sessions', <Category />, 'category')}
          {renderSection(groupedSessions.subcategory, 'Subcategory Sessions', <SubdirectoryArrowRight />, 'subcategory')}
          {renderSection(groupedSessions.series, 'Series Sessions', <Collections />, 'series')}
          {renderSection(groupedSessions.challenge, 'Challenge Sessions', <EmojiEvents />, 'challenge')}
          {renderSection(groupedSessions.audiobook, 'Audiobook Sessions', <MenuBook />, 'audiobook')}
          {renderSection(groupedSessions.unparented, 'Unparented Sessions', null, 'unparented')}
        </Box>
      ) : (
        renderFullListTable()
      )}
    </Box>
  );
};

export default SessionsTable;
